<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="case-detail-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/cases')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <!-- Loading -->
      <div v-if="isLoading" class="loading-state">{{ $t('common.loading') }}</div>

      <template v-else-if="caseData">
        <!-- Header -->
        <div class="detail-header">
          <div>
            <h1 class="page-title">{{ caseData.case_number }}</h1>
            <p class="page-subtitle">{{ caseData.deceased_name }}</p>
          </div>
          <div class="header-actions">
            <CaseStatusBadge :status="caseData.status" />
            <IntercomButton
              v-if="canGenerateReviewLink"
              variant="secondary"
              size="sm"
              :loading="isGeneratingLink"
              @click="handleGenerateReviewLink"
            >
              {{ $t('case.generateReviewLink') }}
            </IntercomButton>
            <IntercomButton
              v-if="canDeliver"
              variant="primary"
              size="sm"
              :loading="isDelivering"
              @click="handleDeliver"
            >
              {{ $t('case.deliver') }}
            </IntercomButton>
            <a
              v-if="canSendResult"
              :href="resultWhatsappLink"
              target="_blank"
              class="whatsapp-result-btn"
            >
              {{ $t('case.sendResultWhatsApp') }}
            </a>
            <CaseActions
              :status="caseData.status"
              @cancel="handleCancel"
              @respond="showRespondModal = true"
              @dispute="showDisputeModal = true"
            />
          </div>
        </div>

        <!-- Tabs -->
        <div class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-btn"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab: Overview -->
        <div v-if="activeTab === 'overview'" class="tab-content">
          <div class="overview-grid">
            <!-- Deceased Info -->
            <IntercomCard class="info-card">
              <h4>{{ $t('case.deceased') }}</h4>
              <div class="info-rows">
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceasedName') }}</span>
                  <span class="info-value">{{ caseData.deceased_name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceasedGender') }}</span>
                  <span class="info-value">{{ caseData.deceased_gender }}</span>
                </div>
                <div class="info-row" v-if="caseData.date_of_death">
                  <span class="info-label">{{ $t('case.dateOfDeath') }}</span>
                  <span class="info-value">{{ formatDate(caseData.date_of_death) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.school') }}</span>
                  <span class="info-value">{{ caseData.school }}</span>
                </div>
              </div>
            </IntercomCard>

            <!-- Heirs -->
            <IntercomCard class="info-card">
              <h4>{{ $t('case.heirs') }} ({{ caseData.heirs?.length || 0 }})</h4>
              <div class="info-rows">
                <div
                  v-for="heir in caseData.heirs"
                  :key="heir.id"
                  class="info-row"
                >
                  <span class="info-label">{{ getHeirLabel(heir.heir_category) }}</span>
                  <span class="info-value">
                    {{ heir.heir_count }}
                    <span v-if="heir.is_blocked" class="blocked-tag">blocked</span>
                  </span>
                </div>
              </div>
            </IntercomCard>

            <!-- Estate -->
            <IntercomCard class="info-card" v-if="caseData.estate_value">
              <h4>{{ $t('case.estate') }}</h4>
              <div class="info-rows">
                <div class="info-row">
                  <span class="info-label">{{ $t('calculator.estateValue') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.estate_value) }}</span>
                </div>
                <div class="info-row" v-if="caseData.funeral_expenses">
                  <span class="info-label">{{ $t('calculator.funeralExpenses') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.funeral_expenses) }}</span>
                </div>
                <div class="info-row" v-if="caseData.debts">
                  <span class="info-label">{{ $t('calculator.debts') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.debts) }}</span>
                </div>
                <div class="info-row" v-if="caseData.unpaid_mahr">
                  <span class="info-label">{{ $t('calculator.unpaidMahr') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.unpaid_mahr) }}</span>
                </div>
                <div class="info-row" v-if="caseData.wasiyyah_amount">
                  <span class="info-label">{{ $t('calculator.wasiyyahAmount') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.wasiyyah_amount) }}</span>
                </div>
              </div>
            </IntercomCard>

            <!-- Scholar & Timeline -->
            <IntercomCard class="info-card">
              <h4>Status</h4>
              <div class="info-rows">
                <div class="info-row" v-if="caseData.scholar">
                  <span class="info-label">{{ $t('case.scholar') }}</span>
                  <span class="info-value">{{ caseData.scholar.name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.submittedAt') }}</span>
                  <span class="info-value">{{ formatDateTime(caseData.submitted_at) }}</span>
                </div>
                <div class="info-row" v-if="caseData.assigned_at">
                  <span class="info-label">{{ $t('case.assignedAt') }}</span>
                  <span class="info-value">{{ formatDateTime(caseData.assigned_at) }}</span>
                </div>
                <div class="info-row" v-if="caseData.evaluated_at">
                  <span class="info-label">{{ $t('case.evaluatedAt') }}</span>
                  <span class="info-value">{{ formatDateTime(caseData.evaluated_at) }}</span>
                </div>
                <div class="info-row" v-if="caseData.delivered_at">
                  <span class="info-label">{{ $t('case.deliveredAt') }}</span>
                  <span class="info-value">{{ formatDateTime(caseData.delivered_at) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.source') }}</span>
                  <span class="info-value">{{ caseData.source }}</span>
                </div>
              </div>
            </IntercomCard>
          </div>
        </div>

        <!-- Tab: Calculation -->
        <div v-if="activeTab === 'calculation'" class="tab-content">
          <IntercomCard class="tab-card">
            <div v-if="isLoadingCalc">{{ $t('common.loading') }}</div>
            <CalculationResult v-else-if="calculation" :result="calculation" />
            <p v-else class="empty-text">{{ $t('result.noShares') }}</p>
          </IntercomCard>
        </div>

        <!-- Tab: Reviews -->
        <div v-if="activeTab === 'reviews'" class="tab-content">
          <IntercomCard class="tab-card">
            <div v-if="isLoadingReviews">{{ $t('common.loading') }}</div>
            <div v-else-if="reviews.length === 0" class="empty-text">No reviews yet</div>
            <div v-else class="reviews-list">
              <div v-for="review in reviews" :key="review.id" class="review-item">
                <div class="review-header">
                  <span class="review-action-badge" :class="review.action">
                    {{ review.action.replace(/_/g, ' ') }}
                  </span>
                  <span class="review-date">{{ formatDateTime(review.created_at) }}</span>
                </div>
                <p v-if="review.reasoning" class="review-reasoning">{{ review.reasoning }}</p>
                <p v-if="review.info_questions" class="review-questions">
                  <strong>Questions:</strong> {{ review.info_questions }}
                </p>
                <div v-if="review.document_url" class="review-document">
                  <a :href="review.document_url" target="_blank" class="document-link">
                    <img :src="review.document_url" alt="Scholar's sealed letter" class="document-thumbnail" />
                    <span class="document-label">{{ $t('review.viewDocument') }}</span>
                  </a>
                </div>
              </div>
            </div>
          </IntercomCard>
        </div>

        <!-- Tab: Notes -->
        <div v-if="activeTab === 'notes'" class="tab-content">
          <IntercomCard class="tab-card">
            <CaseNotesThread :notes="notes" />

            <!-- Add Note Form -->
            <div class="add-note-form">
              <textarea
                v-model="newNote"
                :placeholder="$t('case.notesPlaceholder')"
                class="textarea-input"
                rows="3"
              ></textarea>
              <div class="note-actions">
                <label class="internal-check">
                  <input type="checkbox" v-model="noteIsInternal" />
                  {{ $t('case.internalNote') }}
                </label>
                <IntercomButton
                  variant="primary"
                  size="sm"
                  :disabled="!newNote.trim()"
                  @click="handleAddNote"
                >
                  {{ $t('case.addNote') }}
                </IntercomButton>
              </div>
            </div>
          </IntercomCard>
        </div>

        <!-- Tab: History -->
        <div v-if="activeTab === 'history'" class="tab-content">
          <IntercomCard class="tab-card">
            <CaseStatusTimeline :history="history" />
          </IntercomCard>
        </div>
      </template>

      <!-- Respond Modal -->
      <IntercomModal
        v-model:show="showRespondModal"
        :title="$t('case.respondToInfo')"
      >
        <div class="modal-form">
          <textarea
            v-model="respondText"
            :placeholder="$t('case.responsePlaceholder')"
            class="textarea-input"
            rows="5"
          ></textarea>
          <div class="modal-actions">
            <IntercomButton variant="secondary" @click="showRespondModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton
              variant="primary"
              :disabled="!respondText.trim()"
              @click="handleRespond"
            >
              {{ $t('common.submit') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>

      <!-- Dispute Modal -->
      <IntercomModal
        v-model:show="showDisputeModal"
        :title="$t('case.disputeCase')"
      >
        <div class="modal-form">
          <label>{{ $t('case.disputeReason') }}</label>
          <textarea
            v-model="disputeReason"
            :placeholder="$t('case.disputePlaceholder')"
            class="textarea-input"
            rows="5"
          ></textarea>
          <div class="modal-actions">
            <IntercomButton variant="secondary" @click="showDisputeModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton
              variant="warning"
              :disabled="!disputeReason.trim()"
              @click="handleDispute"
            >
              {{ $t('case.disputeCase') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>

      <!-- Review Link Modal -->
      <IntercomModal
        v-model:show="showReviewLinkModal"
        :title="$t('queue.reviewLinkReady')"
      >
        <div class="link-modal-body">
          <p class="link-success-text">{{ $t('queue.linkGenerated') }}</p>

          <div class="link-box">
            <input
              type="text"
              :value="reviewLinkUrl"
              readonly
              class="link-input"
            />
            <IntercomButton variant="ghost" size="sm" @click="copyReviewLink">
              {{ linkCopied ? $t('queue.copied') : $t('queue.copyLink') }}
            </IntercomButton>
          </div>

          <a
            v-if="reviewLinkScholarPhone"
            :href="whatsappLink"
            target="_blank"
            class="whatsapp-link"
          >
            {{ $t('queue.sendViaWhatsApp') }} ({{ reviewLinkScholarName }})
          </a>

          <p class="link-expiry">
            {{ $t('queue.linkExpires') }}: {{ reviewLinkExpiry }}
          </p>
        </div>

        <template #footer>
          <IntercomButton variant="primary" @click="showReviewLinkModal = false">
            {{ $t('common.close') }}
          </IntercomButton>
        </template>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDialog } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomModal,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import CaseStatusBadge from '../components/CaseStatusBadge.vue'
import CaseActions from '../components/CaseActions.vue'
import CalculationResult from '../components/CalculationResult.vue'
import CaseNotesThread from '../components/CaseNotesThread.vue'
import CaseStatusTimeline from '../components/CaseStatusTimeline.vue'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const dialog = useDialog()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { navSections } = useNavigation()
const {
  getCase,
  getCaseCalculation,
  getCaseReviews,
  getCaseNotes,
  addCaseNote,
  getCaseHistory,
  cancelCase,
  disputeCase,
  respondToInfoRequest,
  deliverCase,
  generateReviewLink,
} = useFaraid()

// State
const caseData = ref(null)
const calculation = ref(null)
const notes = ref([])
const history = ref([])
const isLoading = ref(true)
const isLoadingCalc = ref(false)
const activeTab = ref('overview')

// Modals
const showRespondModal = ref(false)
const showDisputeModal = ref(false)
const respondText = ref('')
const disputeReason = ref('')

// Review link
const showReviewLinkModal = ref(false)
const reviewLinkUrl = ref('')
const reviewLinkScholarName = ref('')
const reviewLinkScholarPhone = ref('')
const reviewLinkExpiry = ref('')
const linkCopied = ref(false)
const isGeneratingLink = ref(false)

// Deliver
const isDelivering = ref(false)

// Notes
const newNote = ref('')
const noteIsInternal = ref(false)

const tabs = [
  { id: 'overview', label: t('case.overview') },
  { id: 'calculation', label: t('case.calculation') },
  { id: 'reviews', label: t('case.reviews') },
  { id: 'notes', label: t('case.notesTab') },
  { id: 'history', label: t('case.history') },
]

// Reviews
const reviews = ref([])
const isLoadingReviews = ref(false)

const heirLabelMap = {
  husband: 'heir.husband', wife: 'heir.wife', son: 'heir.son', daughter: 'heir.daughter',
  father: 'heir.father', mother: 'heir.mother', sons_son: 'heir.sonsSon',
  sons_daughter: 'heir.sonsDaughter', fathers_father: 'heir.fathersFather',
  paternal_grandmother: 'heir.paternalGrandmother', maternal_grandmother: 'heir.maternalGrandmother',
  full_brother: 'heir.fullBrother', full_sister: 'heir.fullSister',
  paternal_half_brother: 'heir.paternalHalfBrother', paternal_half_sister: 'heir.paternalHalfSister',
  maternal_half_brother: 'heir.maternalHalfBrother', maternal_half_sister: 'heir.maternalHalfSister',
  full_brothers_son: 'heir.fullBrothersSon', paternal_half_brothers_son: 'heir.paternalHalfBrothersSon',
  full_paternal_uncle: 'heir.fullPaternalUncle', paternal_half_uncle: 'heir.paternalHalfUncle',
  full_cousin: 'heir.fullCousin', paternal_half_cousin: 'heir.paternalHalfCousin',
}

const getHeirLabel = (category) => {
  const key = heirLabelMap[category]
  return key ? t(key) : category.replace(/_/g, ' ')
}

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''
const formatDateTime = (date) => date ? dayjs(date).format('DD MMM YYYY, HH:mm') : ''
const formatCurrency = (value) => {
  if (!value && value !== 0) return ''
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value)
}

// Fetch case
const fetchCase = async () => {
  isLoading.value = true
  try {
    const response = await getCase(route.params.id)
    caseData.value = response.case || response
  } catch (error) {
    console.error('Failed to fetch case:', error)
    toast.error(t('error.fetchFailed'))
  } finally {
    isLoading.value = false
  }
}

// Fetch tab data on demand
const fetchCalculation = async () => {
  isLoadingCalc.value = true
  try {
    const response = await getCaseCalculation(route.params.id)
    calculation.value = response.calculation || response
  } catch (error) {
    console.error('Failed to fetch calculation:', error)
  } finally {
    isLoadingCalc.value = false
  }
}

const fetchNotes = async () => {
  try {
    const response = await getCaseNotes(route.params.id, { include_internal: true })
    notes.value = response.notes || []
  } catch (error) {
    console.error('Failed to fetch notes:', error)
  }
}

const fetchHistory = async () => {
  try {
    const response = await getCaseHistory(route.params.id)
    history.value = response.history || []
  } catch (error) {
    console.error('Failed to fetch history:', error)
  }
}

const fetchReviews = async () => {
  isLoadingReviews.value = true
  try {
    const response = await getCaseReviews(route.params.id)
    reviews.value = response.reviews || []
  } catch (error) {
    console.error('Failed to fetch reviews:', error)
  } finally {
    isLoadingReviews.value = false
  }
}

// Tab change handler
watch(activeTab, (tab) => {
  if (tab === 'calculation' && !calculation.value) fetchCalculation()
  if (tab === 'reviews' && reviews.value.length === 0) fetchReviews()
  if (tab === 'notes' && notes.value.length === 0) fetchNotes()
  if (tab === 'history' && history.value.length === 0) fetchHistory()
})

// Actions
const handleCancel = () => {
  dialog.warning({
    title: t('case.cancelCase'),
    content: t('case.cancelConfirm'),
    positiveText: t('common.confirm'),
    negativeText: t('common.cancel'),
    onPositiveClick: async () => {
      try {
        await cancelCase(route.params.id)
        toast.success(t('success.caseCancelled'))
        fetchCase()
      } catch (error) {
        toast.error(error.message)
      }
    },
  })
}

const handleRespond = async () => {
  try {
    await respondToInfoRequest(route.params.id, { response: respondText.value.trim() })
    toast.success(t('success.infoResponded'))
    showRespondModal.value = false
    respondText.value = ''
    fetchCase()
  } catch (error) {
    toast.error(error.message)
  }
}

const handleDispute = async () => {
  try {
    await disputeCase(route.params.id, { reason: disputeReason.value.trim() })
    toast.success(t('success.caseDisputed'))
    showDisputeModal.value = false
    disputeReason.value = ''
    fetchCase()
  } catch (error) {
    toast.error(error.message)
  }
}

const handleAddNote = async () => {
  try {
    await addCaseNote(route.params.id, {
      content: newNote.value.trim(),
      is_internal: noteIsInternal.value,
    })
    toast.success(t('success.noteAdded'))
    newNote.value = ''
    noteIsInternal.value = false
    fetchNotes()
  } catch (error) {
    toast.error(error.message)
  }
}

// Review link
const reviewableStatuses = ['under_review', 'info_requested', 'info_provided']

const canGenerateReviewLink = computed(() => {
  if (!caseData.value) return false
  return caseData.value.scholar && reviewableStatuses.includes(caseData.value.status)
})

const whatsappLink = computed(() => {
  if (!reviewLinkScholarPhone.value || !reviewLinkUrl.value) return '#'
  const phone = reviewLinkScholarPhone.value.replace(/\D/g, '')
  const text = encodeURIComponent(
    `Assalamu Alaikum,\n\nYou have been assigned a Faraid case for review.\n\nPlease use this link to review and submit your decision:\n${reviewLinkUrl.value}\n\nThis link is valid for 7 days.`
  )
  return `https://wa.me/${phone}?text=${text}`
})

const handleGenerateReviewLink = async () => {
  isGeneratingLink.value = true
  try {
    const response = await generateReviewLink(route.params.id)
    const linkData = response.data || response

    reviewLinkUrl.value = linkData.url
    reviewLinkScholarName.value = linkData.scholar_name
    reviewLinkScholarPhone.value = linkData.scholar_phone || ''
    reviewLinkExpiry.value = linkData.expires_at
      ? dayjs(linkData.expires_at).format('DD MMM YYYY, h:mm A')
      : '7 days'

    showReviewLinkModal.value = true
  } catch (error) {
    toast.error(error.response?.data?.detail || error.message)
  } finally {
    isGeneratingLink.value = false
  }
}

const copyReviewLink = async () => {
  try {
    await navigator.clipboard.writeText(reviewLinkUrl.value)
    linkCopied.value = true
    toast.success(t('queue.linkCopied'))
    setTimeout(() => { linkCopied.value = false }, 2000)
  } catch {
    const input = document.querySelector('.link-input')
    if (input) {
      input.select()
      document.execCommand('copy')
      linkCopied.value = true
      setTimeout(() => { linkCopied.value = false }, 2000)
    }
  }
}

// Deliver case
const deliverableStatuses = ['evaluated', 're_evaluated']
const resultShareableStatuses = ['evaluated', 're_evaluated', 'delivered']

const canDeliver = computed(() => {
  if (!caseData.value) return false
  return deliverableStatuses.includes(caseData.value.status)
})

const canSendResult = computed(() => {
  if (!caseData.value) return false
  return resultShareableStatuses.includes(caseData.value.status) && caseData.value.contact_phone
})

const resultWhatsappLink = computed(() => {
  if (!caseData.value?.contact_phone) return '#'
  const phone = caseData.value.contact_phone.replace(/\D/g, '')
  const c = caseData.value
  const scholarName = c.scholar?.name || 'Our scholar'

  // Build share summary from calculation if available
  let sharesSummary = ''
  if (calculation.value?.shares) {
    sharesSummary = calculation.value.shares
      .filter(s => !s.is_blocked)
      .map(s => `• ${getHeirLabel(s.heir_category)} (×${s.heir_count}): ${s.share_percentage}%`)
      .join('\n')
  }

  const lines = [
    `Assalamu Alaikum,`,
    ``,
    `Your Faraid case *${c.case_number}* for the estate of *${c.deceased_name}* has been reviewed by ${scholarName}.`,
    ``,
    `*School:* ${c.school}`,
  ]

  if (sharesSummary) {
    lines.push(``, `*Share Distribution:*`, sharesSummary)
  }

  if (c.estate_value) {
    lines.push(``, `*Estate Value:* ₹${new Intl.NumberFormat('en-IN').format(c.estate_value)}`)
  }

  // Find latest review with a document URL
  const latestDocReview = [...reviews.value].reverse().find(r => r.document_url)
  if (latestDocReview?.document_url) {
    lines.push(``, `*Scholar's Sealed Letter:*`, latestDocReview.document_url)
  }

  lines.push(
    ``,
    `For the complete certificate and detailed breakdown, please contact us.`,
    ``,
    `JazakAllahu Khairan.`
  )

  const text = encodeURIComponent(lines.join('\n'))
  return `https://wa.me/${phone}?text=${text}`
})

const handleDeliver = async () => {
  isDelivering.value = true
  try {
    await deliverCase(route.params.id)
    toast.success(t('success.caseDelivered'))
    fetchCase()

    // If calculation not loaded yet, load it so the WhatsApp message has share data
    if (!calculation.value) {
      fetchCalculation()
    }
  } catch (error) {
    toast.error(error.response?.data?.detail || error.message)
  } finally {
    isDelivering.value = false
  }
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(async () => {
  fetchBusiness()
  await fetchCase()
  // Auto-fetch calculation and reviews for evaluated/delivered cases so WhatsApp message has share data + document URLs
  if (caseData.value && resultShareableStatuses.includes(caseData.value.status)) {
    fetchCalculation()
    fetchReviews()
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.case-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.loading-state {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-gray-500);
}

/* Header */
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: var(--space-4) 0 var(--space-6) 0;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--color-gray-200);
  margin-bottom: var(--space-6);
}

.tab-btn {
  padding: var(--space-3) var(--space-5);
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s ease;
}

.tab-btn:hover {
  color: var(--color-charcoal);
}

.tab-btn.active {
  color: var(--color-blue);
  border-bottom-color: var(--color-blue);
}

/* Overview Grid */
.overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-5);
}

.info-card {
  padding: var(--space-5);
}

.info-card h4 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-3) 0;
}

.info-rows {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-gray-100);
  font-size: var(--font-size-sm);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: var(--color-gray-500);
}

.info-value {
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.blocked-tag {
  font-size: var(--font-size-xs);
  color: var(--color-error);
  margin-left: var(--space-1);
}

/* Tab content */
.tab-card {
  padding: var(--space-6);
}

/* Notes form */
.add-note-form {
  margin-top: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.textarea-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.textarea-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.note-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-3);
}

.internal-check {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  cursor: pointer;
}

/* Modal form */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.modal-form label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-6);
}

/* WhatsApp Result Button */
.whatsapp-result-btn {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) var(--space-3);
  background: #25d366;
  color: white;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  white-space: nowrap;
  line-height: 1.5;
}

.whatsapp-result-btn:hover {
  background: #1fb855;
}

/* Review Link Modal */
.link-modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.link-success-text {
  color: #059669;
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.link-box {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.link-input {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
}

.whatsapp-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: #25d366;
  color: white;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  text-align: center;
  justify-content: center;
}

.whatsapp-link:hover {
  background: #1fb855;
}

.link-expiry {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0;
}

/* Reviews */
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.review-item {
  padding: var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
}

.review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-2);
}

.review-action-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: capitalize;
  background: var(--color-gray-100);
  color: var(--color-gray-600);
}

.review-action-badge.approved { background: #ecfdf5; color: #059669; }
.review-action-badge.adjusted { background: #eff6ff; color: #2563eb; }
.review-action-badge.manual_override { background: #fef3c7; color: #d97706; }
.review-action-badge.info_requested { background: #fef2f2; color: #dc2626; }
.review-action-badge.picked_up { background: var(--color-gray-100); color: var(--color-gray-600); }

.review-date {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.review-reasoning,
.review-questions {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  margin: var(--space-2) 0 0 0;
}

.review-document {
  margin-top: var(--space-3);
}

.document-link {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  text-decoration: none;
}

.document-thumbnail {
  max-width: 160px;
  max-height: 120px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
  object-fit: cover;
}

.document-label {
  font-size: var(--font-size-xs);
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

@media (max-width: 768px) {
  .case-detail-view {
    padding: var(--space-4);
  }

  .detail-header {
    flex-direction: column;
    gap: var(--space-3);
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }

  .tabs {
    overflow-x: auto;
  }
}
</style>
